'use client';

import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { Link } from '@/i18n/navigation';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { useInterval, useIsClient, useMediaQuery } from 'usehooks-ts';
import CreateForFreeMenuButton from '@/app/[locale]/(create)/components/CreateForFreeMenuButton';
import AutoplayVisibleVideo from '@/components/AutoplayVisibleVideo';
import ViverseButton from '@/components/ViverseButton';
import { useLayoutContext } from '@/context/LayoutContext';
import { cn } from '@/lib/utils';
import ImageAvatar3 from '@/public/images/creator-intro/avatar-2.png';
import ImageAvatar2 from '@/public/images/creator-intro/avatar-3.png';
import ImageAvatar1 from '@/public/images/creator-intro/avatar-4.png';
import ImageCode from '@/public/images/creator-intro/landing-code.png';
import ImageDiamond from '@/public/images/creator-intro/landing-diamond.png';
import ImageRocket from '@/public/images/creator-intro/landing-rocket.png';
const useIntroductions = () => {
  const $ = _c(29);
  const t = useTranslations();
  let t0;
  if ($[0] !== t) {
    t0 = t("creator-intro.banner.introduction-1-title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("creator-intro.banner.introduction-1-description");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      icon: ImageRocket,
      className: "1.5xl:-top-[80px] 1.5xl:right-8 right-0 -top-[10%] md:-top-[16%] 1.5xl:-top-[24%] size-[96px] md:size-[120px]"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1) {
    t3 = {
      title: t0,
      description: t1,
      position: "left",
      className: "1.5xl:-left-[5%] 1.5xl:top-[27%] 3xl:top-[34%] 3xl:-left-[10%]",
      blurEffectClassName: "bg-[#E652AE] 1.5xl:blur-[234px] blur-[100px]",
      titleIcon: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("creator-intro.banner.introduction-2-title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("creator-intro.banner.introduction-2-description");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      images: [ImageAvatar1, ImageAvatar2, ImageAvatar3],
      size: 56
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5) {
    t7 = {
      title: t4,
      description: t5,
      position: "bottom",
      className: "3xl:-bottom-[9%] 1.5xl:-bottom-[20%] 1.5xl:right-[11%]",
      blurEffectClassName: "bg-primary 1.5xl:blur-[234px] blur-[100px]",
      footerIcons: t6
    };
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("creator-intro.banner.introduction-3-title");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("creator-intro.banner.introduction-3-description");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      icon: ImageDiamond,
      className: "1.5xl:bottom-[-40%] bottom-[-32%] md:bottom-[-36%] 1.5xl:left-1/2 1.5xl:translate-x-[-50%] right-[10%] size-[120px] md:size-[160px]"
    };
    t11 = {
      icon: ImageCode,
      className: "1.5xl:-top-6 top-0 right-0 size-[76px]"
    };
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t8 || $[23] !== t9) {
    t12 = {
      title: t8,
      description: t9,
      position: "right",
      className: "1.5xl:-right-[5%] 1.5xl:top-[17%] 3xl:-right-[14%]",
      blurEffectClassName: "bg-[#415FF8] 1.5xl:blur-[234px] blur-[100px]",
      backgroundIcon: t10,
      titleIcon: t11
    };
    $[22] = t8;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t3 || $[27] !== t7) {
    t13 = [t3, t7, t12];
    $[25] = t12;
    $[26] = t3;
    $[27] = t7;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const introductions = t13;
  return introductions;
};
const useSubtitles = () => {
  const $ = _c(17);
  const t = useTranslations();
  const locale = useLocale();
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  let t0;
  if ($[0] !== t) {
    t0 = t("creator-intro.banner-subtitle1");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("creator-intro.banner-subtitle2");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("creator-intro.banner-subtitle3");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = [t0, t1, t2];
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const subtitleList = t3;
  let t4;
  if ($[10] !== subtitleList.length) {
    t4 = () => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentSubtitleIndex(prev => (prev + 1) % subtitleList.length);
        setIsFading(false);
      }, 200);
    };
    $[10] = subtitleList.length;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useInterval(t4, 1000);
  let titleFlex = "flex-[54%]";
  let subtitleFlex = "flex-[46%]";
  bb0: switch (locale) {
    case "en-US":
      {
        titleFlex = "flex-[54%]";
        subtitleFlex = "flex-[46%]";
        break bb0;
      }
    case "zh-TW":
      {
        titleFlex = "flex-[58%]";
        subtitleFlex = "flex-[42%]";
        break bb0;
      }
    case "ja-JP":
      {
        titleFlex = "flex-[46%]";
        subtitleFlex = "flex-[54%]";
        break bb0;
      }
    default:
  }
  const t5 = subtitleList[currentSubtitleIndex];
  let t6;
  if ($[12] !== isFading || $[13] !== subtitleFlex || $[14] !== t5 || $[15] !== titleFlex) {
    t6 = {
      subtitle: t5,
      titleFlex,
      subtitleFlex,
      isFading
    };
    $[12] = isFading;
    $[13] = subtitleFlex;
    $[14] = t5;
    $[15] = titleFlex;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
function HeroBanner() {
  const $ = _c(57);
  const {
    headerHeight
  } = useLayoutContext();
  const {
    subtitle,
    titleFlex,
    subtitleFlex,
    isFading
  } = useSubtitles();
  const t = useTranslations();
  const isClient = useIsClient();
  const introductions = useIntroductions();
  const matches1440 = useMediaQuery("(min-width: 1440px)");
  let t0;
  if ($[0] !== headerHeight) {
    t0 = {
      marginTop: headerHeight
    };
    $[0] = headerHeight;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== headerHeight || $[3] !== isClient || $[4] !== matches1440) {
    t1 = isClient && {
      style: {
        top: headerHeight * -1 - (matches1440 ? 7 : 17),
        transform: "translate3d(0, 0, 0)"
      }
    };
    $[2] = headerHeight;
    $[3] = isClient;
    $[4] = matches1440;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    };
    t3 = {
      duration: 1,
      delay: 0.5
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1) {
    t4 = <motion.div className="bg-primary bg-creator-intro-hero-introduce-card 1.5xl:h-[120px] 1.5xl:w-[957px] 1.5xl:blur-[106px] absolute z-interactive flex h-[83px] w-[238px] flex-col gap-3 rounded-3xl p-6 blur-[54px]" {...t1} initial="hidden" animate="visible" exit="hidden" variants={t2} transition={t3} />;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    };
    t6 = {
      duration: 1
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== titleFlex) {
    t7 = cn("header-1 text-right text-[46px] md:text-[64px] md:tracking-[0.4px]", titleFlex);
    $[12] = titleFlex;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("creator-intro.banner-title");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <p className={t7}>{t8}</p>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = !isFading;
  let t11;
  if ($[19] !== isFading || $[20] !== subtitleFlex || $[21] !== t10) {
    t11 = cn("header-1 bg-gradient-title-2 bg-clip-text text-[46px] text-transparent transition-opacity duration-200 md:text-[64px] md:tracking-[0.4px]", subtitleFlex, {
      "opacity-60": isFading,
      "opacity-100": t10
    });
    $[19] = isFading;
    $[20] = subtitleFlex;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== subtitle || $[24] !== t11) {
    t12 = <p className={t11}>{subtitle}</p>;
    $[23] = subtitle;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t9) {
    t13 = <div className="1.5xl:flex-row 1.5xl:gap-3 flex w-full flex-col items-center justify-center">{t9}{t12}</div>;
    $[26] = t12;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("creator-intro.banner-description");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <p className="subheader-1 text-vive-primary-20 flex max-w-[900px] text-center text-[18px] leading-[32px] tracking-[0.15px] md:text-[24px]">{t14}</p>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <CreateForFreeMenuButton size="filter" variant="default" className="1.5xl:px-[24px] 1.5xl:py-3 1.5xl:text-[22px] min-h-[48px] py-3 text-[18px] whitespace-normal" />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t) {
    t17 = t("creator-intro.banner.cta-2");
    $[34] = t;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ExternalLink className="stroke-vive-primary-20 ml-2" size={24} />;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t17) {
    t19 = <div className="flex flex-col items-center gap-4 md:flex-row">{t16}<Link target="_blank" href="https://docs.viverse.com/"><ViverseButton size="filter" variant="secondary" className="1.5xl:px-[24px] 1.5xl:py-3 1.5xl:text-[22px] min-h-[48px] py-3 text-[18px] whitespace-normal">{t17}{t18}</ViverseButton></Link></div>;
    $[37] = t17;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t13 || $[40] !== t15 || $[41] !== t19) {
    t20 = <motion.div className="flex w-full flex-col items-center gap-4 px-4 py-6 md:px-[80px]" initial="hidden" animate="visible" exit="hidden" variants={t5} transition={t6}>{t13}{t15}{t19}</motion.div>;
    $[39] = t13;
    $[40] = t15;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    };
    t22 = {
      duration: 1
    };
    $[43] = t21;
    $[44] = t22;
  } else {
    t21 = $[43];
    t22 = $[44];
  }
  let t23;
  let t24;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    };
    t24 = {
      duration: 1,
      delay: 0.2
    };
    $[45] = t23;
    $[46] = t24;
  } else {
    t23 = $[45];
    t24 = $[46];
  }
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <motion.div initial="hidden" animate="visible" exit="hidden" variants={t23} transition={t24} className="1.5xl:aspect-1140/641 1.5xl:rounded-[48px] 3xl:aspect-1440/810 relative flex aspect-360/202 h-fit w-full flex-1 overflow-hidden rounded-2xl md:aspect-620/348"><AutoplayVisibleVideo className="bg-creator-intro-hero-introduce-card absolute inset-0 flex size-full flex-col gap-3 object-cover" src="/videos/creator-intro/create-landing.mp4" autoPlay={true} muted={true} loop={true} playsInline={true} poster="/images/creator-intro/landing-hero-thumbnail.png" /></motion.div>;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== introductions) {
    t26 = introductions.map(_temp);
    $[48] = introductions;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t26) {
    t27 = <motion.div className="1.5xl:px-[7px] 3xl:px-[170px] flex w-full flex-1 px-4 md:px-[74px]" initial="hidden" animate="visible" exit="hidden" variants={t21} transition={t22}><div className="relative flex flex-1 flex-col gap-4 md:gap-6">{t25}{t26}</div></motion.div>;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t0 || $[53] !== t20 || $[54] !== t27 || $[55] !== t4) {
    t28 = <section className="3x:mb-[263px] 1.5xl:mb-[253px] 1.5xl:gap-10 1.5xl:px-[80px] 1.5xl:pt-[40px] 3xl:px-[120px] relative mb-[140px] flex min-h-dvh flex-1 flex-col items-center justify-center gap-8 pt-[36px] pb-0 md:mb-[180px] md:pt-3" style={t0}>{t4}{t20}{t27}</section>;
    $[52] = t0;
    $[53] = t20;
    $[54] = t27;
    $[55] = t4;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  return t28;
}
function _temp(intro, index) {
  return <motion.div key={intro.title} variants={{
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1
    }
  }} initial="hidden" animate="visible" exit="hidden" transition={{
    delay: index * 0.25,
    duration: 1
  }} className={cn("1.5xl:absolute 1.5xl:max-w-[368px] relative z-interactive flex w-full rounded-3xl", intro.className)}><div className={cn("1.5xl:inset-0 absolute inset-1/4", intro.blurEffectClassName)} />{intro.backgroundIcon && <div className={cn("absolute", intro.backgroundIcon.className)}><OptimizedImage alt={intro.title} src={intro.backgroundIcon.icon} className="object-cover" fill={true} sizes="(max-width: 768px) 120px, 160px" /></div>}<div className="bg-creator-intro-hero-introduce-card relative flex min-h-[194px] flex-1 flex-col justify-center gap-3 rounded-3xl p-3 backdrop-blur-xl md:min-h-[216px] md:p-6"><div className="flex"><p className="md:header-3 header-4">{intro.title}</p>{intro.titleIcon && <div className={cn("absolute", intro.titleIcon.className)}><OptimizedImage alt={intro.title} src={intro.titleIcon.icon} className="object-cover" fill={true} sizes="(max-width: 768px) 120px, 160px" /></div>}</div><p className="md:body-1 body-2 text-vive-primary-30">{intro.description}</p>{intro.footerIcons && <div className="flex">{intro.footerIcons.images.map((image, index_0) => <OptimizedImage alt="avatar" key={index_0} src={image} width={intro.footerIcons.size} height={intro.footerIcons.size} className={cn("bg-primary rounded-full p-1", {
          "-ml-2": index_0 > 0
        })} />)}</div>}</div></motion.div>;
}
export default HeroBanner;